import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { aiTokenService } from '../../../services/aiTokenService';

const RENEWAL_STYLES = {
  green: { backgroundColor: '#DCFCE7', borderColor: '#86EFAC', color: '#22C55E', dot: 'bg-emerald-500' },
  yellow: { backgroundColor: '#FEF3C7', borderColor: '#FDE68A', color: '#F59E0B', dot: 'bg-amber-500' },
  red: { backgroundColor: '#FEE2E2', borderColor: '#FCA5A5', color: '#EF4444', dot: 'bg-red-500' },
};

// Thresholds per spec: >4 days green, 3-4 yellow, 0-2 red "due soon", <0 red "overdue".
function getRenewalStatus(daysRemaining) {
  if (daysRemaining === null) return null;
  if (daysRemaining < 0) return { level: 'red', label: 'MidJourney subscription renewal overdue' };
  if (daysRemaining <= 2) return { level: 'red', label: 'MidJourney payment due soon' };
  if (daysRemaining <= 4) return { level: 'yellow', label: 'MidJourney renewal coming soon' };
  return { level: 'green', label: 'Active' };
}

function formatDaysRemaining(daysRemaining) {
  if (daysRemaining < 0) {
    const overdue = Math.abs(daysRemaining);
    return `Overdue by ${overdue} day${overdue === 1 ? '' : 's'}`;
  }
  if (daysRemaining === 0) return 'Due today';
  return `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`;
}

const MidjourneyCard = () => {
  const { user } = useAuth();
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRenewal, setEditingRenewal] = useState(false);
  const [draftRenewal, setDraftRenewal] = useState('');
  const [savingRenewal, setSavingRenewal] = useState(false);
  const [renewalError, setRenewalError] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    (async () => {
      setLoading(true);
      const { data } = await aiTokenService.getConnections('midjourney');
      if (active) {
        setConnection(data?.[0] || null);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user?.id]);

  const isConnected = !!connection;
  const isValid = connection?.status === 'valid';

  const renewalDate = connection?.renewal_date ? parseISO(connection.renewal_date) : null;
  const daysRemaining = renewalDate ? differenceInCalendarDays(renewalDate, new Date()) : null;
  const renewalStatus = getRenewalStatus(daysRemaining);
  const renewalStyle = renewalStatus ? RENEWAL_STYLES[renewalStatus.level] : null;

  const startEditingRenewal = () => {
    setRenewalError('');
    setDraftRenewal(connection?.renewal_date || '');
    setEditingRenewal(true);
  };

  const handleSaveRenewal = async () => {
    if (!draftRenewal) return;
    setSavingRenewal(true);
    setRenewalError('');
    const { data, error } = await aiTokenService.updateMidjourneyRenewalDate(draftRenewal);
    setSavingRenewal(false);
    if (error) {
      setRenewalError(error.message || 'Failed to save renewal date');
      return;
    }
    setConnection((prev) => (prev ? { ...prev, renewal_date: data } : prev));
    setEditingRenewal(false);
  };

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-violet-50">
            <Icon name="Sparkles" size={15} className="text-violet-600" />
          </div>
          <p className="text-sm font-semibold text-[#0F172A]">Midjourney</p>
        </div>
        <Link to="/tools/settings" className="text-[11px] text-[#94A3B8] hover:text-[#0F172A] transition-colors">
          <Icon name="Edit2" size={12} />
        </Link>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-4 bg-slate-100 rounded w-full" />
          <div className="h-4 bg-slate-100 rounded w-2/3" />
        </div>
      ) : isConnected ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#64748B]">Discord token</span>
            <span className="text-xs font-semibold text-[#0F172A]">••••••••{connection.last_four}</span>
          </div>
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium"
            style={isValid
              ? { backgroundColor: '#DCFCE7', borderColor: '#86EFAC', color: '#22C55E' }
              : { backgroundColor: '#FEF3C7', borderColor: '#FDE68A', color: '#F59E0B' }}
          >
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isValid ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {isValid ? 'Connected' : 'Connected — not tested yet'}
          </div>

          <div className="pt-3 border-t border-[#E5E7EB] space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#64748B]">Subscription renewal</span>
              {!editingRenewal && (
                <button
                  type="button"
                  onClick={startEditingRenewal}
                  className="text-[11px] font-medium text-violet-600 hover:text-violet-700 transition-colors"
                >
                  {connection?.renewal_date ? 'Edit' : 'Set renewal date'}
                </button>
              )}
            </div>

            {editingRenewal ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={draftRenewal}
                    onChange={(e) => setDraftRenewal(e.target.value)}
                    className="flex-1 text-xs border border-[#E5E7EB] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  />
                  <button
                    type="button"
                    disabled={!draftRenewal || savingRenewal}
                    onClick={handleSaveRenewal}
                    className="text-[11px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 rounded-lg px-2.5 py-1.5 transition-colors"
                  >
                    {savingRenewal ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingRenewal(false)}
                    className="text-[11px] font-medium text-[#64748B] hover:text-[#0F172A] px-2 py-1.5 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                {renewalError && <p className="text-[11px] text-red-500">{renewalError}</p>}
              </div>
            ) : connection?.renewal_date ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#64748B]">Renewal date</span>
                  <span className="text-xs font-semibold text-[#0F172A]">{format(renewalDate, 'MMMM d, yyyy')}</span>
                </div>
                <p className="text-xs text-[#0F172A] font-medium">{formatDaysRemaining(daysRemaining)}</p>
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium"
                  style={renewalStyle}
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${renewalStyle.dot}`} />
                  {renewalStatus.label}
                </div>
              </div>
            ) : (
              <p className="text-xs text-[#94A3B8]">No renewal date set — add one so Gymplix can remind you before it expires.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center py-4 gap-2 text-center">
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium w-full justify-center"
            style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB', color: '#94A3B8' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
            Not connected
          </div>
          <p className="text-xs text-[#94A3B8]">Connect your own Discord automation to generate images</p>
          <Link
            to="/tools/settings"
            className="text-xs font-semibold text-violet-600 hover:text-violet-700 underline transition-colors"
          >
            Connect in Settings
          </Link>
        </div>
      )}
    </div>
  );
};

export default MidjourneyCard;
